import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { fetchAdminSites, type AdminSiteRow } from '@/services/siteApi';

// Admin: every Hey Joe Site with its owner, hosting, size and this month's traffic.
// Traffic comes from CloudFront logs, rolled up every 3 hours (siteHosting.js sweepBandwidth).
const mb = (b: number) => (b >= 1e9 ? `${(b / 1e9).toFixed(1)} GB` : b >= 1e6 ? `${(b / 1e6).toFixed(1)} MB` : b > 0 ? `${Math.max(1, Math.round(b / 1e3))} KB` : '0');
const plan = (h: AdminSiteRow['hosting']) => {
  if (!h) return 'None';
  const kind = h.mode === 'charter_free' ? 'Charter free year' : h.mode === 'comp' ? 'Free (comp)' : h.mode === 'item' ? `On Plus (${h.interval || 'month'})` : h.plan === 'standalone' ? 'Standalone' : 'Add-on';
  return h.status === 'active' ? kind : `${kind}, ${h.status.replace('_', ' ')}`;
};
const cell: React.CSSProperties = { padding: '10px 12px', borderBottom: '1px solid var(--hj-hairline)', textAlign: 'left', whiteSpace: 'nowrap', fontSize: 14 };

const SitesAdminPage: React.FC = () => {
  const [data, setData] = useState<{ sites: AdminSiteRow[]; softCapBytes: number } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { fetchAdminSites().then(setData).catch((e) => setErr(e.message)); }, []);
  const total = data?.sites.reduce((n, s) => n + s.monthBytes, 0) || 0;
  return (
    <main className="mx-auto" style={{ maxWidth: 1100, padding: '24px 16px 96px', background: 'var(--hj-bg)', color: 'var(--hj-ink)', minHeight: 'calc(100vh - 68px)' }}>
      <h1 style={{ fontSize: 26, fontWeight: 700, margin: '0 0 8px' }}>Hey Joe Sites</h1>
      <p style={{ margin: '0 0 16px', fontSize: 14, color: 'var(--hj-ink-2)' }}>
        This month so far, updated every 3 hours. <Link to="/admin/site-review" style={{ color: 'var(--hj-ink)', textDecoration: 'underline' }}>Uploads held for review</Link>
      </p>
      {err && <p style={{ color: 'var(--hj-pink-ink)' }}>{err}</p>}
      {!data && !err && <Loader2 className="w-5 h-5 animate-spin" />}
      {data && (
        <>
          <p style={{ margin: '0 0 12px', fontSize: 15 }}>{data.sites.length} {data.sites.length === 1 ? 'site' : 'sites'} · {mb(total)} served this month</p>
          <div style={{ overflowX: 'auto', border: '1px solid var(--hj-hairline)', borderRadius: 'var(--hj-r-md)', background: 'var(--hj-surface-1)' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead><tr>{['Site', 'Owner', 'Status', 'Hosting', 'Size', 'Page views', 'Served'].map((h) => <th key={h} style={{ ...cell, fontWeight: 600 }}>{h}</th>)}</tr></thead>
              <tbody>
                {data.sites.map((s) => (
                  <tr key={s.id}>
                    <td style={cell}><a href={`https://${s.primaryHost}`} target="_blank" rel="noreferrer" style={{ color: 'var(--hj-ink)', textDecoration: 'underline' }}>{s.primaryHost}</a>{s.liveDomains > 0 && s.primaryHost.endsWith('.heyjoesites.com') ? ` +${s.liveDomains} domain${s.liveDomains === 1 ? '' : 's'}` : ''}</td>
                    <td style={cell}>{s.ownerEmail}</td>
                    <td style={cell}>{s.status}</td>
                    <td style={cell}>{plan(s.hosting)}</td>
                    <td style={cell}>{mb(s.siteBytes)}</td>
                    <td style={cell}>{s.monthPages.toLocaleString()}</td>
                    <td style={{ ...cell, fontWeight: s.monthBytes >= data.softCapBytes ? 700 : 400, color: s.monthBytes >= data.softCapBytes ? 'var(--hj-pink-ink)' : undefined }}>{mb(s.monthBytes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </main>
  );
};

export default SitesAdminPage;
